import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Styles/OrderPage.css';

function OrderPage({ tableInfo, audioEnabled = true }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [cart, setCart] = useState({});
  const [cartNotes, setCartNotes] = useState({});
  const [orderPanelCollapsed, setOrderPanelCollapsed] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [orderState, setOrderState] = useState(null);
  const orderStateRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);
  const [hasPlayedWelcome, setHasPlayedWelcome] = useState(false);
  const [shouldStartVoiceAfterLoad, setShouldStartVoiceAfterLoad] = useState(false);
  const [tableName, setTableName] = useState(null);
  const [partySize, setPartySize] = useState(null);
  const [audioEnabledState, setAudioEnabledState] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [modalQuantity, setModalQuantity] = useState(1);
  const [countAnimating, setCountAnimating] = useState(false);
  const prevTotalRef = useRef(0);
  const [debugMode, setDebugMode] = useState(false);

  const minSwipeDistance = 50;

  const recognitionRef = useRef(null);
  const navigate = useNavigate();
  const resturantName = localStorage.getItem('resturantName')
  const isFetchingMenu = useRef(false)
  const isShowingMenu = useRef(false)
  const isMenuLoaded = useRef(false)
  const isProcessingNLP = useRef(false)
  const lastProcessedText = useRef('')
  const lastProcessedTime = useRef(0)
  

  const menuContentRef = useRef(null);
  const categoryRefs = useRef({});

  useEffect(() => {
    // Check for table info from props or sessionStorage
    if (tableInfo) {
      setTableName(tableInfo.table_name);
      setPartySize(tableInfo.party_size);
    } else {
      const storedTableInfo = sessionStorage.getItem('tableInfo');
      if (storedTableInfo) {
        const info = JSON.parse(storedTableInfo);
        setTableName(info.table_name);
        setPartySize(info.party_size);
      }
    }

  }, [tableInfo]);

  useEffect(() => {
    // Initialize debug mode
    const urlParams = new URLSearchParams(window.location.search);
    const isDebugMode = urlParams.get('debug') !== 'false' && 
                       localStorage.getItem('waiterDebugMode') !== 'false' &&
                       process.env.REACT_APP_DISABLE_LOGGING !== 'true';
    setDebugMode(isDebugMode);
  }, []);

  useEffect(() => {
    // Reset speaking flag when component mounts
    window.isSpeaking = false;
    
    return () => {
      window.isSpeaking = false;
      if (recognitionRef.current) {

        recognitionRef.current.stop();
        recognitionRef.current = null;
        
      }
      window.isStartingRecognition = false;
    };
  }, []);

  useEffect(() => {
    if (audioEnabled || sessionStorage.getItem('userHasInteracted') === 'true') {
      setAudioEnabledState(true);
    }
  }, [audioEnabled]);

let lastSpokenText = '';
let lastSpokenTime = 0;

const speakText = async (text) => {
  // Skip if we just spoke this exact text within 1 second
  const now = Date.now();
  if (text === lastSpokenText && now - lastSpokenTime < 1000) {
    return;
  }
  
  // Always reset the speaking flag at the start
  window.isSpeaking = false;
  
  if (!audioEnabledState) {
    return;
  }
  
  lastSpokenText = text;
  lastSpokenTime = now;
  
  try {
    // Cancel any existing speech first
    window.speechSynthesis.cancel();

    window.isSpeaking = true;
    
    const response = await fetch('https://waiter-ai.onrender.com/api/tts/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sentence: text })
    });
    
    if (response.ok) {
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      return new Promise((resolve) => {
        audio.onended = () => {
          logDebug('tts', 'TTS audio ended');
          URL.revokeObjectURL(audioUrl);
          window.isSpeaking = false; // Reset flag when done
          // Small delay to ensure flag is propagated
          setTimeout(() => resolve(), 50);
        };
        
        audio.onerror = (error) => {
          URL.revokeObjectURL(audioUrl);
          window.isSpeaking = false; // Reset flag on error
          setTimeout(() => resolve(), 50);
        };
        
        audio.play().catch(error => {
          window.isSpeaking = false; // Reset flag on play failure
          setTimeout(() => resolve(), 50);
        });
      });
    } else {
      window.isSpeaking = false; // Reset flag on request failure
      return;
    }
    
  } catch (error) {
    window.isSpeaking = false; // Reset flag on any error
    return;
  }
};

const processModificationItem = async (modificationData) => {
  
  if (!modificationData || !modificationData.target_cart_key) {
    return {
      success: false,
      message: "Invalid modification request."
    };
  }
  
  const { type, target_cart_key, ...details } = modificationData;
  
  if (type === 'size_change') {
    return await processSizeChange(target_cart_key, details);
  } else if (type === 'ingredient_change') {
    return await processIngredientChange(target_cart_key, details);
  } else {
    return {
      success: false,
      message: "Unknown modification type."
    };
  }
};
const processSizeChange = async (targetCartKey, details) => {
  
  // Use the same reliable state capture approach as processCancellationItem
  const getCurrentCartState = () => {
    // Use refs to get the current state immediately
    const currentCart = cart; // Direct reference
    const currentNotes = cartNotes; // Direct reference
    
    return { 
      cartState: { ...currentCart }, 
      cartNotes: { ...currentNotes } 
    };
  };
  
  const { cartState: currentCart, cartNotes: currentCartNotes } = await getCurrentCartState();
  
  // Check if the target item exists in cart
  if (!currentCart[targetCartKey]) {
    return {
      success: false,
      message: "Item not found in cart."
    };
  }
  
  const currentQuantity = currentCart[targetCartKey];
  const currentNotes = currentCartNotes[targetCartKey] || null;
  
  // Find the original product info
  const baseCartKey = targetCartKey.replace(/-notes-[^-]+$/, '');
  const hyphenCount = (baseCartKey.match(/-/g) || []).length;
  
  let originalProduct = null;
  
  if (hyphenCount === 9) {
    const parts = baseCartKey.split('-');
    if (parts.length === 10) {
      const productId = parts.slice(0, 5).join('-');
      originalProduct = products.find(p => p.id === productId);
    }
  } else if (hyphenCount === 4) {
    originalProduct = products.find(p => p.id === baseCartKey);
  }
  
  if (!originalProduct) {
    return {
      success: false,
      message: "Could not find original product."
    };
  }
  
  // Find the new variant
  const newVariant = variants.find(v => v.id === details.new_variant_id);
  if (!newVariant) {
    return {
      success: false,
      message: "New variant not found."
    };
  }

  // Remove old item from cart
  setCart(prev => {
    const newCart = { ...prev };
    delete newCart[targetCartKey];
    return newCart;
  });
  
  // Remove old notes
  setCartNotes(prev => {
    const newNotes = { ...prev };
    delete newNotes[targetCartKey];
    return newNotes;
  });

  // Determine new notes
  let newNotes = currentNotes;
  if (details.ingredient_modifications && details.ingredient_modifications.notes) {
    if (currentNotes) {
      newNotes = `${currentNotes}, ${details.ingredient_modifications.notes}`;
    } else {
      newNotes = details.ingredient_modifications.notes;
    }
  }

  // Get the quantity to modify (from backend)
  const modifyQuantity = details.modify_quantity || 1;
  const remainingQuantity = currentQuantity - modifyQuantity;

  // Add back the remaining items with original variant (if any)
  if (remainingQuantity > 0) {
    // First, we need to get the original variant
    const originalVariantId = targetCartKey.includes('-') ? 
      targetCartKey.split('-').slice(5, 10).join('-') : null;
    
    if (originalVariantId) {
      const originalVariant = variants.find(v => v.id === originalVariantId);
      if (originalVariant) {
        for (let i = 0; i < remainingQuantity; i++) {
          addToCart(originalProduct, originalVariant, currentNotes);
        }
      }
    }
  }
  
  for (let i = 0; i < modifyQuantity; i++) {
    addToCart(originalProduct, newVariant, newNotes);
  }
  // Update the success message to reflect partial modifications
  let message;
  if (modifyQuantity === currentQuantity) {
    message = `Changed ${originalProduct.name} to ${newVariant.name}.`;
  } else {
    message = `Changed ${modifyQuantity} of your ${originalProduct.name}s to ${newVariant.name}.`;
  }

  return {
    success: true,
    message: message
  };
};

const onTouchStart = (e) => {
  setTouchEnd(null); // Reset touch end
  setTouchStart(e.targetTouches[0].clientY);
};

const onTouchMove = (e) => {
  setTouchEnd(e.targetTouches[0].clientY);
};

const onTouchEnd = () => {
  if (!touchStart || !touchEnd) return;
  
  const distance = touchStart - touchEnd;
  const isSwipeUp = distance > minSwipeDistance;
  const isSwipeDown = distance < -minSwipeDistance;
  
  if (isSwipeUp && orderPanelCollapsed) {
    // Swipe up when collapsed = expand
    setOrderPanelCollapsed(false);
  }
  if (isSwipeDown && !orderPanelCollapsed) {
    // Swipe down when expanded = collapse
    setOrderPanelCollapsed(true);
  }
};
const processIngredientChange = async (targetCartKey, details) => {
  
  // Use the same reliable state capture approach
  const getCurrentCartState = () => {
    return new Promise((resolve) => {
      setCart(currentCart => {
        setCartNotes(currentNotes => {
          resolve({ 
            cartState: { ...currentCart }, 
            cartNotes: { ...currentNotes } 
          });
          return currentNotes;
        });
        return currentCart;
      });
    });
  };
  
  const { cartState: currentCart, cartNotes: currentCartNotes } = await getCurrentCartState();
  
  // Check if the target item exists in cart
  if (!currentCart[targetCartKey]) {
    return {
      success: false,
      message: "Item not found in your order."
    };
  }
  
  const currentQuantity = currentCart[targetCartKey];
  const currentNotes = currentCartNotes[targetCartKey] || null;
  
  // Find the original product and variant info
  const baseCartKey = targetCartKey.replace(/-notes-[^-]+$/, '');
  const hyphenCount = (baseCartKey.match(/-/g) || []).length;
  
  let originalProduct = null;
  let originalVariant = null;
  
  if (hyphenCount === 9) {
    const parts = baseCartKey.split('-');
    if (parts.length === 10) {
      const productId = parts.slice(0, 5).join('-');
      const variantId = parts.slice(5, 10).join('-');
      originalProduct = products.find(p => p.id === productId);
      originalVariant = variants.find(v => v.id === variantId);
    }
  } else if (hyphenCount === 4) {
    originalProduct = products.find(p => p.id === baseCartKey);
    originalVariant = null;
  }
  
  if (!originalProduct) {
    return {
      success: false,
      message: "Could not find original product."
    };
  }
  
  // Remove old item from cart
  setCart(prev => {
    const newCart = { ...prev };
    delete newCart[targetCartKey];
    return newCart;
  });
  
  // Remove old notes
  setCartNotes(prev => {
    const newNotes = { ...prev };
    delete newNotes[targetCartKey];
    return newNotes;
  });
  
  // Determine new notes
  let newNotes = null;
  if (details.notes) {
    if (currentNotes) {
      // Combine existing notes with new modifications
      newNotes = `${currentNotes}, ${details.notes}`;
    } else {
      newNotes = details.notes;
    }
  } else {
    newNotes = currentNotes;
  }
  
  // Add new item with modified notes
  for (let i = 0; i < currentQuantity; i++) {
    addToCart(originalProduct, originalVariant, newNotes);
  }
  
  return {
    success: true,
    message: `Modified ${originalProduct.name} ingredients.`
  };
};


useEffect(() => {
    const audioEnabledSession = sessionStorage.getItem('audioEnabled');
    if (audioEnabledSession === 'true') {
      setAudioEnabledState(true);
      // Check if already played to prevent duplicates
      if (isMenuLoaded.current && !hasPlayedWelcome && !isShowingMenu.current) {
        showingMenu();
        setHasPlayedWelcome(true);
      }
    }
  }, [isMenuLoaded.current, hasPlayedWelcome]); // Add hasPlayedWelcome to dependencies


 useEffect(() => {
    const handleScroll = () => {
      if (!menuContentRef.current) return;
      
    
      const scrollPosition = menuContentRef.current.scrollTop;
      const scrollHeight = menuContentRef.current.scrollHeight;
      const clientHeight = menuContentRef.current.clientHeight;

      const isAtBottom = scrollPosition + clientHeight >= scrollHeight - 10;
      
      
      // Find which category is currently in view
      let currentCategory = null;
      const categoryElements = [];

      categories.forEach(category => {
        const element = categoryRefs.current[category.category_id];
        if (element) {
          const rect = element.getBoundingClientRect();
          const containerRect = menuContentRef.current.getBoundingClientRect();
          const relativeTop = rect.top - containerRect.top;
          
          categoryElements.push({
            id: category.category_id,
            top: relativeTop,
            element: element
          });
        }
      });

      // Special cases for top and bottom
      if (scrollPosition <= 10 && categoryElements.length > 0) {
        // At the very top, select first category
        currentCategory = categoryElements[0].id;
      } else if (isAtBottom && categoryElements.length > 0) {
        // At the very bottom, select last category
        currentCategory = categoryElements[categoryElements.length - 1].id;
      } else {
        // Calculate which category is most visible
        const containerHeight = menuContentRef.current.clientHeight;
        const viewportMiddle = containerHeight / 2;
        
        let bestCategory = null;
        let bestScore = -1;
        
        categoryElements.forEach((category, index) => {
          const categoryTop = category.top;
          const categoryElement = category.element;
          const categoryHeight = categoryElement.offsetHeight;
          const categoryBottom = categoryTop + categoryHeight;
          
          // Calculate how much of this category is visible
          const visibleTop = Math.max(0, categoryTop);
          const visibleBottom = Math.min(containerHeight, categoryBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibilityRatio = visibleHeight / categoryHeight;
          
          // Score based on multiple factors
          let score = 0;
          
          // 1. Visibility ratio (how much of the category is visible)
          score += visibilityRatio * 100;
          
          // 2. Position relative to viewport middle
          // Categories closer to middle of viewport get bonus points
          const categoryMiddle = categoryTop + (categoryHeight / 2);
          const distanceFromMiddle = Math.abs(categoryMiddle - viewportMiddle);
          const middleBonus = Math.max(0, 100 - (distanceFromMiddle / containerHeight * 100));
          score += middleBonus * 0.5;
          
          // 3. If category title is past halfway point, give it priority
          if (categoryTop < viewportMiddle && categoryBottom > 0) {
            score += 50;
          }
          
          if (score > bestScore) {
            bestScore = score;
            bestCategory = category.id;
          }
        });
        
        currentCategory = bestCategory || (categoryElements.length > 0 ? categoryElements[0].id : null);
      }
      
      if (currentCategory && currentCategory !== activeCategory) {
        setActiveCategory(currentCategory);
      }
    };
    
    const menuContent = menuContentRef.current;
    if (menuContent) {
      menuContent.addEventListener('scroll', handleScroll);
      // Trigger initial check after a small delay to ensure elements are rendered
      setTimeout(handleScroll, 100);
      return () => menuContent.removeEventListener('scroll', handleScroll);
    }
  }, [categories, activeCategory]);

  useEffect(() => {
    if (sessionId) return;
    let currentSessionId = sessionStorage.getItem('orderSessionId');
    
    if (!currentSessionId) {
      // No session exists - create new one and play welcome
      currentSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('orderSessionId', currentSessionId);
      setHasPlayedWelcome(false);
    } else {
      // Session exists - check last activity and welcome status
      const lastActivity = sessionStorage.getItem('lastActivity');
      const welcomePlayed = sessionStorage.getItem('welcomePlayed');
      const now = Date.now();
      const timeSinceLastActivity = now - (lastActivity ? parseInt(lastActivity) : 0);
      


      if (!welcomePlayed || timeSinceLastActivity > 300000) {
        setHasPlayedWelcome(false);
        sessionStorage.setItem('welcomePlayed', 'true');
      } else {
        setHasPlayedWelcome(true);
      }
    }
    
    sessionStorage.setItem('lastActivity', Date.now().toString());
    setSessionId(currentSessionId);
  }, [sessionId]);

  useEffect(() => {
    const savedCart = sessionStorage.getItem('currentOrder');
    const savedNotes = sessionStorage.getItem('cartNotes');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCart(parsedCart);
        
        if (savedNotes) {
          const parsedNotes = JSON.parse(savedNotes);
          setCartNotes(parsedNotes);
        }
        
        // Don't auto-expand when loading saved cart
      } catch (error) {
        sessionStorage.removeItem('currentOrder');
        sessionStorage.removeItem('cartNotes');
      }
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('lastActivity', Date.now().toString());
    
    if (Object.keys(cart).length > 0) {
      sessionStorage.setItem('currentOrder', JSON.stringify(cart));
      sessionStorage.setItem('cartNotes', JSON.stringify(cartNotes));
      
      const sessionData = {
        sessionId,
        cart,
        cartNotes,
        timestamp: new Date().toISOString(),
        restaurantName: resturantName
      };
      sessionStorage.setItem('orderSession', JSON.stringify(sessionData));
    } else {
      sessionStorage.removeItem('currentOrder');
      sessionStorage.removeItem('cartNotes');
      sessionStorage.removeItem('orderSession');
    }
  }, [cart, cartNotes, sessionId, resturantName]);

  useEffect(() => {
    if (products.length > 0 && variants.length > 0 && shouldStartVoiceAfterLoad && isMenuLoaded.current) {
      setShouldStartVoiceAfterLoad(false);
      
      if (!hasPlayedWelcome) {
        showingMenu();
        setHasPlayedWelcome(true);
      } else {
        startVoiceOrder();
      }
    }
  }, [products, variants, shouldStartVoiceAfterLoad, hasPlayedWelcome]);

  useEffect(() => {
    if (products.length > 0 && variants.length > 0 && isMenuLoaded.current) {
      const pendingOrderItem = sessionStorage.getItem('pendingOrderItem');
      if (pendingOrderItem) {
        try {
          const orderItem = JSON.parse(pendingOrderItem);
          processOrderItem(orderItem);
        } catch (error) {
          sessionStorage.removeItem('pendingOrderItem');
        }
      }
    }
  }, [products, variants]);

  const scrollToCategory = (categoryId) => {
    const element = categoryRefs.current[categoryId];
    if (element && menuContentRef.current) {
      const offsetTop = element.offsetTop - 20;
      menuContentRef.current.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
    setActiveCategory(categoryId);
  };


  const getProductsByCategory = (categoryId) => {
    if (!categoryId || !products) return [];
    return products.filter(product => product.category_id === categoryId);
  };

  const getVariantsByProduct = (productId) => {
    if (!productId || !variants) return [];
    return variants.filter(variant => variant.product_id === productId);
  };

  const getIngredientsByProduct = (productId) => {
    if (!productId || !ingredients) return [];
    return ingredients.filter(ingredient => ingredient.product_id === productId);
  };

  const addToCart = (product, variant = null, notes = null) => {

    if (!product) {
      return;
    }

    let baseCartKey;
    if (product.has_varity && variant) {
      baseCartKey = `${product.id}-${variant.id}`;
    } else if (!product.has_varity || !variant) {
      baseCartKey = product.id;
    } else {
      return;
    }
    

    let cartKey = baseCartKey;
    if (notes && notes.length > 0) {  
        const notesStr = Array.isArray(notes) ? notes.sort().join(',') : notes;
        const notesHash = btoa(notesStr).substring(0, 8);
        cartKey = `${baseCartKey}-notes-${notesHash}`;
    } else {
      const existingKeys = Object.keys(cart).filter(key => key.startsWith(baseCartKey));
      const existingWithoutNotes = existingKeys.find(key => !key.includes('-notes-'));
      if (existingWithoutNotes) {
        cartKey = existingWithoutNotes;
      }
    }

    setCart(prev => {
      const newCart = {
        ...prev,
        [cartKey]: (prev[cartKey] || 0) + 1
      };
      return newCart;
    });

    // Handle notes
    if (notes) {
      setCartNotes(prev => ({
        ...prev,
        [cartKey]: notes
      }));
    }
  };

  const removeFromCart = (cartKey) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[cartKey] > 1) {
        newCart[cartKey]--;
      } else {
        delete newCart[cartKey];
        setCartNotes(prevNotes => {
          const newNotes = { ...prevNotes };
          delete newNotes[cartKey];
          return newNotes;
        });
      }
      return newCart;
    });
  };

  const clearCart = () => {
    setCart({});
    setCartNotes({});
    sessionStorage.removeItem('currentOrder');
    sessionStorage.removeItem('cartNotes');
    sessionStorage.removeItem('orderSession');
  };

  const getTotalItems = () => {
    return Object.values(cart).reduce((sum, count) => sum + count, 0);
  };
  
  // Track count changes for animation
  useEffect(() => {
    const currentTotal = getTotalItems();
    if (currentTotal > prevTotalRef.current && orderPanelCollapsed) {
      setCountAnimating(true);
      setTimeout(() => setCountAnimating(false), 300);
    }
    prevTotalRef.current = currentTotal;
  }, [cart, orderPanelCollapsed]);

  const getTotalPrice = () => {
    
    return Object.entries(cart).reduce((total, [cartKey, count]) => {
      
      // Remove notes suffix to get the base cart key for price lookup
      const baseCartKey = cartKey.replace(/-notes-[^-]+$/, '');
      
      // Check if this is a variant cart key (product-variant) by counting hyphens in base key
      const hyphenCount = (baseCartKey.match(/-/g) || []).length;
      
      if (hyphenCount === 9) {
        const parts = baseCartKey.split('-');
        if (parts.length === 10) {
          const variantId = parts.slice(5, 10).join('-');
          const variant = variants.find(v => v.id === variantId);
          if (variant && variant.price && typeof variant.price === 'number') {
            return total + (variant.price * count);
          }
        }
      } else if (hyphenCount === 4) {
        const product = products.find(p => p.id === baseCartKey);

        if (product && product.price && typeof product.price === 'number') {
          return total + (product.price * count);
        } 
      }
      
      return total;
    }, 0);
  };

  const getCartItems = () => {
    
    const items = Object.entries(cart).map(([cartKey, count]) => {

      const baseCartKey = cartKey.replace(/-notes-[^-]+$/, '');
      
      const hyphenCount = (baseCartKey.match(/-/g) || []).length;

      const itemNotes = cartNotes[cartKey] || null;
      
      if (hyphenCount === 9) {
        const parts = baseCartKey.split('-');
        
        if (parts.length === 10) {
          const productId = parts.slice(0, 5).join('-');
          const variantId = parts.slice(5, 10).join('-');

          
          const product = products.find(p => p.id === productId);
          const variant = variants.find(v => v.id === variantId);
          
          if (product && variant && typeof variant.price === 'number') {
            return {
              cartKey,
              count,
              product,
              variant,
              name: `${product.name} - ${variant.name}`,
              price: variant.price,
              subtotal: variant.price * count,
              notes: itemNotes
            };
          }
        }
      } else if (hyphenCount === 4) {
        const product = products.find(p => p.id === baseCartKey);
        
        if (product && typeof product.price === 'number') {
          
          return {
            cartKey,
            count,
            product,
            variant: null,
            name: product.name,
            price: product.price,
            subtotal: product.price * count,
            notes: itemNotes
          };
      
        }
      }
      
      return null;
    }).filter(Boolean);
    
    return items;
  };

  // FIXED CANCELLATION METHOD
  const processCancellationItem = async (cancelItem) => {
    
    if (!cancelItem || (!cancelItem.item_name && !cancelItem.cart_key)) {
      return {
        success: false,
        message: "Invalid cancellation request."
      };
    }
    
    // If a specific cart_key is provided, use it directly
    if (cancelItem.cart_key) {
      
      // Get current cart state
      const getCurrentCartState = () => {
        return new Promise((resolve) => {
          setCart(currentCart => {
            resolve({ ...currentCart });
            return currentCart;
          });
        });
      };
      
      const currentCart = await getCurrentCartState();
      
      if (!currentCart[cancelItem.cart_key]) {
        return {
          success: false,
          message: "Item not found in your order."
        };
      }
      
      const itemCount = currentCart[cancelItem.cart_key];
      const quantityToRemove = cancelItem.quantity || itemCount;
      
      // Remove the specified quantity
      for (let i = 0; i < Math.min(quantityToRemove, itemCount); i++) {
        removeFromCart(cancelItem.cart_key);
      }
      
      return {
        success: true,
        message: `Removed ${Math.min(quantityToRemove, itemCount)} item(s) from your order.`
      };
    }
    
    // Otherwise, use the existing logic for item name-based cancellation
    const getCurrentCartState = () => {
      return new Promise((resolve) => {
        setCart(currentCart => {
          setCartNotes(currentNotes => {
            resolve({ 
              cartState: { ...currentCart }, 
              cartNotes: { ...currentNotes } 
            });
            return currentNotes;
          });
          return currentCart;
        });
      });
    };

    const { cartState: currentCart, cartNotes: currentCartNotes } = await getCurrentCartState();
    
    const currentCartItems = Object.entries(currentCart).map(([cartKey, count]) => {

      const baseCartKey = cartKey.replace(/-notes-[^-]+$/, '');
      const hyphenCount = (baseCartKey.match(/-/g) || []).length;
      
      // Get notes for this cart item
      const itemNotes = currentCartNotes[cartKey] || null;
      
      if (hyphenCount === 9) {
        const parts = baseCartKey.split('-');
        if (parts.length === 10) {
          const productId = parts.slice(0, 5).join('-');
          const variantId = parts.slice(5, 10).join('-');
          
          const product = products.find(p => p.id === productId);
          const variant = variants.find(v => v.id === variantId);
          
          if (product && variant && typeof variant.price === 'number') {
            return {
              cartKey,
              count,
              product,
              variant,
              name: `${product.name} - ${variant.name}`,
              product_name: product.name,
              variant_name: variant.name,
              price: variant.price,
              subtotal: variant.price * count,
              notes: itemNotes,
              quantity: count
            };
          }
        }
      } else if (hyphenCount === 4) {
        const product = products.find(p => p.id === baseCartKey);
        if (product && typeof product.price === 'number') {
          return {
            cartKey,
            count,
            product,
            variant: null,
            name: product.name,
            product_name: product.name,
            variant_name: null,
            price: product.price,
            subtotal: product.price * count,
            notes: itemNotes,
            quantity: count
          };
        }
      }
      return null;
    }).filter(Boolean);
    
    const result = cancelOrderItemWithCurrentData(cancelItem.item_name, cancelItem.quantity, currentCartItems);
    
    return result;
  };

const cancelOrderItemWithCurrentData = (itemName, quantity = null, cartItems) => {
    
    if (cartItems.length === 0) {
      return {
        success: false,
        message: `Your order is empty. There's nothing to cancel.`
      };
    }
    
    // Find items that match the name
    const matchingItems = cartItems.filter(item => {
      const itemNameLower = item.name.toLowerCase();
      const searchNameLower = itemName.toLowerCase();
      
      const productNameLower = item.product ? item.product.name.toLowerCase() : '';
      
      // Check multiple matching strategies
      const exactMatch = itemNameLower === searchNameLower;
      const partialMatch = itemNameLower.includes(searchNameLower) || searchNameLower.includes(itemNameLower);
      const productMatch = productNameLower === searchNameLower;
      const productPartialMatch = productNameLower.includes(searchNameLower) || searchNameLower.includes(productNameLower);
      
      return exactMatch || partialMatch || productMatch || productPartialMatch;
    });
    
    if (matchingItems.length === 0) {
      return {
        success: false,
        message: `I don't see any ${itemName} in your order to cancel.`
      };
    }
    
    let totalCanceled = 0;
    let canceledItems = [];
    
    // Sort matching items to prioritize exact matches
    matchingItems.sort((a, b) => {
      // If one has the exact variant specified and the other doesn't, prioritize the exact match
      const aExact = a.name.toLowerCase() === itemName.toLowerCase();
      const bExact = b.name.toLowerCase() === itemName.toLowerCase();
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return 0;
    });
    
    // Cancel the matching items
    if (quantity && typeof quantity === 'number') {
      let remainingToCancel = quantity;
      
      for (const item of matchingItems) {
        if (remainingToCancel <= 0) break;
        
        const itemsToRemove = Math.min(remainingToCancel, item.count);
        
        // Remove items from cart
        for (let i = 0; i < itemsToRemove; i++) {
          removeFromCart(item.cartKey);
        }
        
        totalCanceled += itemsToRemove;
        remainingToCancel -= itemsToRemove;
        
        canceledItems.push({
          name: item.name,
          quantity: itemsToRemove,
          notes: item.notes
        });
      }
      
      return {
        success: true,
        message: `I removed ${totalCanceled} ${itemName}${totalCanceled > 1 ? 's' : ''} from your order.`,
        canceledItems
      };
    } else {
      // Cancel all matching items
      for (const item of matchingItems) {
        
        // Remove all instances of this item
        for (let i = 0; i < item.count; i++) {
          removeFromCart(item.cartKey);
        }
        
        totalCanceled += item.count;
        canceledItems.push({
          name: item.name,
          quantity: item.count,
          notes: item.notes
        });
      }
      
      return {
        success: true,
        message: `I removed all ${itemName}${totalCanceled > 1 ? 's' : ''} from your order (${totalCanceled} item${totalCanceled > 1 ? 's' : ''}).`,
        canceledItems
      };
    }
};

  const navigateToSummary = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    navigate('/summary');
  };

  const showingMenu = async () => {
    if (!isMenuLoaded.current) {
      return;
    }
    if (isShowingMenu.current) {
      return;
    }
    isShowingMenu.current = true

    try {
      
      if (audioEnabledState) {
        await speakText('What would you like to order?');
      } else {
        setTimeout(() => 3000);
      }

    } catch (error) {
    } finally {
      isShowingMenu.current = false;

      if (products.length > 0 && variants.length > 0) {
        // Start voice recognition immediately
        startVoiceOrder();
      } else {
        setShouldStartVoiceAfterLoad(true);
      }
    }
  };


const startVoiceOrder = () => {
  // Add a flag to prevent multiple simultaneous starts
  if (window.isStartingRecognition) {
    return;
  }
  
  // Don't start if we're processing NLP or TTS is speaking
  if (isProcessingNLP.current || window.isSpeaking) {
    logDebug('recognition', 'Blocked from starting voice recognition', {
      isProcessingNLP: isProcessingNLP.current,
      isSpeaking: window.isSpeaking
    });
    return;
  }
  
  window.isStartingRecognition = true;
  
  if (!isMenuLoaded.current) {
    window.isStartingRecognition = false;
    return;
  }

  if (products.length === 0 || variants.length === 0) {
    window.isStartingRecognition = false;
    return;
  }

  // Clean up any existing recognition
  if (recognitionRef.current) {
    
    recognitionRef.current.stop();
    recognitionRef.current = null;

    // Minimal delay to ensure clean stop
    setTimeout(() => {
      startVoiceOrderInternal();
    }, 100);
  } else {
    startVoiceOrderInternal();
  }
};

const startVoiceOrderInternal = () => {

    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      // For iOS Safari, we need to use non-continuous mode
      recognitionRef.current.continuous = !isIOS; // Continuous mode doesn't work well on iOS
      recognitionRef.current.interimResults = !isIOS; // iOS has issues with interim results
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.maxAlternatives = 1;
      
      logDebug('recognition', 'Speech recognition settings', {
        continuous: recognitionRef.current.continuous,
        interimResults: recognitionRef.current.interimResults,
        isMobile: isMobile,
        isIOS: isIOS,
        isSafari: isSafari
      });

      // Add silence detection timeout for continuous mode
      let silenceTimer;
      let finalTranscript = '';
      let isProcessing = false;

      recognitionRef.current.onstart = () => {
        logDebug('recognition', 'Recognition started');
        setIsListening(true);
        isProcessing = false;
        finalTranscript = '';
      };

      recognitionRef.current.onresult = (event) => {
        logDebug('recognition', 'Speech recognition event', {
          resultIndex: event.resultIndex,
          resultsLength: event.results.length
        });
        
        // iOS Safari handling - process immediately since it's not continuous
        if (isIOS) {
          const result = event.results[0][0].transcript;
          logDebug('recognition', 'iOS transcript', { transcript: result });
          
          if (result && result.trim().length > 0) {
            processVoiceOrder(result);
          }
          return;
        }
        
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }

        let interimTranscript = '';
        let fullTranscript = '';

        // Get the latest complete transcript from all results
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (i === event.results.length - 1) {
            // Log only the latest result
          }
          
          if (event.results[i].isFinal) {
            fullTranscript = transcript; // Use the latest final result
          } else {
            interimTranscript = transcript;
          }
        }
        
        // Use the latest transcript, not accumulated
        const currentText = (fullTranscript || interimTranscript).trim();
        logDebug('recognition', 'Current text', { 
          currentText: currentText,
          fullTranscript: fullTranscript,
          interimTranscript: interimTranscript
        });
        
        // Update finalTranscript with the latest text for onend handler
        finalTranscript = currentText;
        
        // Simple approach: just wait 2 seconds after any speech
        silenceTimer = setTimeout(() => {
          if (!isProcessing && currentText.length > 0) {
            isProcessing = true;
            
            if (recognitionRef.current) {
              recognitionRef.current.stop();
            }
            
            processVoiceOrder(currentText);
          }
        }, 2000); // Always wait 2 seconds of silence
      };

      recognitionRef.current.onerror = (event) => {
        
        // Handle specific error types
        switch(event.error) {
          case 'aborted':
            setIsListening(false);
            // Don't restart immediately for aborted errors
            break;
            
          case 'no-speech':
            if (orderStateRef.current && orderStateRef.current.awaiting) {
              // Restart recognition for state-based responses
              setTimeout(() => {
                startVoiceOrder();
              }, 2000);
            }
            setIsListening(false);
            break;
            
          case 'audio-capture':
          case 'not-allowed':
            alert('Microphone permission denied. Please enable microphone access.');
            setIsListening(false);
            break;
            
          case 'network':
            setIsListening(false);
            setTimeout(() => {
              startVoiceOrder();
            }, 2000);
            break;
            
          default:
            setIsListening(false);
        }
      };
    recognitionRef.current.onend = () => {
      logDebug('recognition', 'Recognition ended', { finalTranscript });
      setIsListening(false);
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      
      // Process any final transcript that hasn't been processed yet
      if (finalTranscript && finalTranscript.trim().length > 0 && !isProcessing) {
        logDebug('recognition', 'Processing final transcript from onend', { finalTranscript });
        processVoiceOrder(finalTranscript);
      }
      
      // Auto-restart for iOS or if we're still expecting input
      if (isIOS || (orderStateRef.current && orderStateRef.current.awaiting)) {
        logDebug('recognition', 'Restarting recognition');
        setTimeout(() => {
          if (!window.isSpeaking && audioEnabledState) {
            startVoiceOrder();
          }
        }, 500);
      }
    };

    // Try to start recognition
    try {
      recognitionRef.current.start();
      window.isStartingRecognition = false;
    } catch (error) {
      window.isStartingRecognition = false;
      setIsListening(false);
    }
  } else {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      alert('Voice ordering requires microphone permissions. Please ensure Safari has microphone access in Settings > Safari > Microphone.');
    } else {
      alert('Speech recognition not supported in this browser. Please use Chrome or Edge for the best experience.');
    }
    window.isStartingRecognition = false;
  }
};
  

  // Also update stopVoiceOrder to handle the timer
  const stopVoiceOrder = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

// Helper function to log debug info to voice logs
const logDebug = (type, message, data = {}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.get('debug') !== 'false' && 
                   localStorage.getItem('waiterDebugMode') !== 'false' &&
                   process.env.REACT_APP_DISABLE_LOGGING !== 'true';
  
  if (debugMode) {
    try {
      // Use existing voice log endpoint to capture debug info
      fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://waiter-ai.onrender.com'}/api/log-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: `[DEBUG-${type.toUpperCase()}] ${message}`,
          debug_data: data,
          timestamp: new Date().toISOString(),
          sessionId: sessionId,
          tableName: tableName
        })
      });
    } catch (err) {
      // Silent fail
    }
  }
};

const processVoiceOrder = async (text) => {
  logDebug('voice', 'processVoiceOrder called', { text });
  
  // Prevent duplicate processing
  if (isProcessingNLP.current) {
    logDebug('voice', 'Already processing, skipping');
    return;
  }
  
  // Don't process empty text
  if (!text || text.trim().length === 0) {
    logDebug('voice', 'Empty text, skipping');
    isProcessingNLP.current = false;
    return;
  }
  
  // Debounce identical requests within 500ms
  const now = Date.now();
  if (text === lastProcessedText.current && now - lastProcessedTime.current < 500) {
    logDebug('voice', 'Duplicate request within 500ms, skipping');
    return;
  }
  
  lastProcessedText.current = text;
  lastProcessedTime.current = now;
  isProcessingNLP.current = true;
  
  try {
    // Logging is ON by default during testing phase
    // Can be disabled with:
    // 1. URL: ?debug=false
    // 2. LocalStorage: localStorage.setItem('waiterDebugMode', 'false')
    // 3. Environment variable: REACT_APP_DISABLE_LOGGING=true
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') !== 'false' && 
                     localStorage.getItem('waiterDebugMode') !== 'false' &&
                     process.env.REACT_APP_DISABLE_LOGGING !== 'true';
    
    if (debugMode) {
      // Send to backend for persistent logging (no console logs)
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://waiter-ai.onrender.com'}/api/log-voice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: text,
            timestamp: new Date().toISOString(),
            sessionId: sessionId,
            tableName: tableName
          })
        });
      } catch (err) {
        // Silent fail - no console logging
      }
    }
    
    const currentState = orderStateRef.current;
    
    // Get current cart items to send to NLP system
    // Use refs or direct state access to ensure we get current values
    const getCurrentCartState = () => {
      return new Promise((resolve) => {
        setCart(currentCart => {
          setCartNotes(currentNotes => {
            resolve({ 
              cartState: { ...currentCart }, 
              cartNotes: { ...currentNotes } 
            });
            return currentNotes;
          });
          return currentCart;
        });
      });
    };
    
    const { cartState: currentCartState, cartNotes: currentCartNotes } = await getCurrentCartState();

    
    // Build cart summary from current state
    const cartSummary = [];
    if (Object.keys(currentCartState).length > 0) {
      Object.entries(currentCartState).forEach(([cartKey, count]) => {
        
        // Remove notes suffix to get the base cart key for product/variant lookup
        const baseCartKey = cartKey.replace(/-notes-[^-]+$/, '');
        const hyphenCount = (baseCartKey.match(/-/g) || []).length;
        
        // Get notes for this cart item
        const itemNotes = currentCartNotes[cartKey] || null;

        
        if (hyphenCount === 9) {
          const parts = baseCartKey.split('-');
          if (parts.length === 10) {
            const productId = parts.slice(0, 5).join('-');
            const variantId = parts.slice(5, 10).join('-');
            
            const product = products.find(p => p.id === productId);
            const variant = variants.find(v => v.id === variantId);
            
            if (product && variant) {
              const cartItem = {
                name: `${product.name} - ${variant.name}`,
                product_name: product.name,
                variant_name: variant.name,
                quantity: count,
                cart_key: cartKey,
                notes: itemNotes
              };
              cartSummary.push(cartItem);
            }
          }
        } else if (hyphenCount === 4) {
          const product = products.find(p => p.id === baseCartKey);

          if (product) {
            const cartItem = {
              name: product.name,
              product_name: product.name,
              variant_name: null,
              quantity: count,
              cart_key: cartKey,
              notes: itemNotes
            };
            cartSummary.push(cartItem);
          }
        }
      });
    }

    const nlpRequest = {
      text: text,
      current_state: currentState,
      current_cart: cartSummary
    };
    
    // Send debug logs to backend if enabled
    if (debugMode) {
      fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://waiter-ai.onrender.com'}/api/log-nlp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'request',
          data: nlpRequest,
          sessionId: sessionId,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
    }
    
    
    const restaurantId = sessionStorage.getItem('restaurantId');
    const nlpUrl = restaurantId 
      ? `https://waiter-ai.onrender.com/api/nlp/process_order/${restaurantId}`
      : 'https://waiter-ai.onrender.com/api/nlp/process_order';
    const response = await fetch(nlpUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(nlpRequest)
    });
    
    const result = await response.json();
    
    // Send NLP response to backend for logging if enabled
    if (debugMode) {
      fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://waiter-ai.onrender.com'}/api/log-nlp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'response',
          data: result,
          sessionId: sessionId,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
    }
    
    if (result.finalize_order) {
      
      await speakText(result.response);
      
      setTimeout(() => {
        navigateToSummary();
      }, 1000);
      
      return;
    }
    
    // Handle cancellation requests
    if (result.intent === 'cancel' && result.cancel_item) {

      const cancellationResult = await processCancellationItem(result.cancel_item);
      
      let responseMessage;
      if (cancellationResult && cancellationResult.message) {
        responseMessage = cancellationResult.message;
      } else if (result.response) {
        responseMessage = result.response;
      } else {
        responseMessage = "Item removed from your order.";
      }

      await speakText(responseMessage);
      
      // Reset the processing flag before starting voice order
      isProcessingNLP.current = false;
      
      // Start recording immediately after TTS finishes
      startVoiceOrder();
      
      return;
    }
    
    if (result.intent === 'modify' && result.modification) {
      
      const modificationResult = await processModificationItem(result.modification);
      
      let responseMessage;
      if (modificationResult && modificationResult.message) {
        responseMessage = modificationResult.message;
      } else if (result.response) {
        responseMessage = result.response;
      } else {
        responseMessage = "Item modified successfully.";
      }
      
      await speakText(responseMessage);
      
      // Reset the processing flag before starting voice order
      isProcessingNLP.current = false;
      
      // Start recording immediately after TTS finishes
      startVoiceOrder();
      
      return;
    }
    if (result.state !== undefined) {
      logDebug('nlp', 'Setting order state from NLP response', { state: result.state });
      setOrderState(result.state);
      orderStateRef.current = result.state;
    }
    
    // Debug: Check if we have order completion data
    
    if (result.complete && result.order_item) {
      processOrderItem(result.order_item);
      
      if (result.state === null || result.state === undefined) {
        setOrderState(null);
        orderStateRef.current = null;
      }
    } else if (result.complete && result.multiple_order_items) {
        for (const orderItem of result.multiple_order_items) {
          processOrderItem(orderItem);
        }
        
        if (result.state === null || result.state === undefined) {
          setOrderState(null);
          orderStateRef.current = null;
        }
      }
    
    if ('speechSynthesis' in window && result && result.response) {
      try {

        if (recognitionRef.current) {
          recognitionRef.current.stop();
          recognitionRef.current = null;
        }
        
        // Wait for TTS to complete
        await speakText(result.response);
        
        // Reset the processing flag before starting voice order
        isProcessingNLP.current = false;
        
        // Start recording immediately after TTS finishes
        startVoiceOrder();
        
      } catch (error) {
        // Start recording immediately on error
        startVoiceOrder();
      }
    }
    
  } catch (error) {
    setOrderState(null);
    orderStateRef.current = null;
    isProcessingNLP.current = false;
    setTimeout(() => {
      startVoiceOrder();
    }, 1000);
  } finally {
    isProcessingNLP.current = false;
  }
};

  const processOrderItem = (orderItem) => {
    
    if (products.length === 0) {
      sessionStorage.setItem('pendingOrderItem', JSON.stringify(orderItem));
      return;
    }

    
    const product = products.find(p => p.id === orderItem.product_id);
    
    if (!product) {
      return;
    }

    // Extract notes from the order item
    const itemNotes = orderItem.notes || null;

    if (!product.has_varity) {
      
      for (let i = 0; i < orderItem.quantity; i++) {
        addToCart(product, null, itemNotes);
      }
      
      // Don't auto-expand when adding items
      return;
    }

    let variant = null;
    if (orderItem.variant_id) {
      variant = variants.find(v => v.id === orderItem.variant_id);
    } else {
      const productVariants = variants.filter(v => v.product_id === orderItem.product_id);
      
      if (orderItem.variant_name) {
        variant = productVariants.find(v => 
          v.name.toLowerCase() === orderItem.variant_name.toLowerCase()
        );
      }
      
      if (!variant && productVariants.length > 0) {
        variant = productVariants[0];
      }
    }
    
    if (variant) {
      
      for (let i = 0; i < orderItem.quantity; i++) {
        addToCart(product, variant, itemNotes);
      }
      
      // Don't auto-expand when adding items
    }
  };

  useEffect(() => {
    if (isFetchingMenu.current || products.length > 0) return;
    
    const fetchMenu = async () => {
      isFetchingMenu.current = true
      try {
        const restaurantId = sessionStorage.getItem('restaurantId');
        const menuUrl = restaurantId
          ? `https://waiter-ai.onrender.com/api/orders/get_menu/${restaurantId}`
          : 'https://waiter-ai.onrender.com/api/orders/get_menu';
        const response = await fetch(menuUrl);

        if (response.status !== 200) {
          throw new Error('Network response was not ok');
        } 
        const result = await response.json();
        
        localStorage.setItem('products', JSON.stringify(result.menu));
        setProducts(result.menu);
        setCategories(result.categories);
        setVariants(result.varients || []);
        setIngredients(result.ingredients || []);
        
        if (result.categories.length > 0 && !activeCategory) {
          setActiveCategory(result.categories[0].category_id);
        }

        isMenuLoaded.current = true;


        return response.status

      } catch (err) {
        const storedProducts = localStorage.getItem('products');
        if (storedProducts) {
          const parsedProducts = JSON.parse(storedProducts);
          setProducts(parsedProducts);
          
          const uniqueCategories = [...new Set(parsedProducts.map(p => p.category_id))]
            .map(id => ({ category_id: id, name: `Category ${id.slice(0, 8)}` }));
          setCategories(uniqueCategories);
          
          if (uniqueCategories.length > 0 && !activeCategory) {
            setActiveCategory(uniqueCategories[0].category_id);
          }
          
          isMenuLoaded.current = true;

        }
        throw err;
      } finally {
        isFetchingMenu.current = false
      }
    };

    const executeSequence = async () => {
      try {
        const status = await fetchMenu(); 

        
        if (status === 200 && isMenuLoaded.current) {
          setShouldStartVoiceAfterLoad(true);
        }
        
      } catch (error) {
      }
    };

    executeSequence();
  }, [hasPlayedWelcome]);



  return (
    <div className="order-app">
      {/* Debug indicator */}
      {debugMode && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          background: 'red',
          color: 'white',
          padding: '5px 10px',
          fontSize: '12px',
          zIndex: 9999
        }}>
          BETA VERSION
        </div>
      )}
      {/* Main Content Area */}
      <div className="main-content">
        {/* Header */}
        <div className="app-header">
          <h1 className="app-title">Menu</h1>
          {/* {tableName && (
            <div className="table-info">
              <span className="table-name">Table: {tableName}</span>
              {partySize && <span className="party-size">Party of {partySize}</span>}
            </div>
          )} */}
        </div>

        {/* Category Navigation */}
        <div className="category-nav">
          <div className="category-buttons">
            {categories.map(category => (
              <button
                key={category.category_id}
                onClick={() => scrollToCategory(category.category_id)}
                className={`category-btn ${activeCategory === category.category_id ? 'active' : ''}`}
              >
                <span className="category-name">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Menu Items Grid with ScrollSpy */}
        <div className="menu-content" ref={menuContentRef}>
          {categories.map(category => (
            <div 
              key={category.category_id} 
              className="category-section"
              ref={el => categoryRefs.current[category.category_id] = el}
            >
              <h2 className="category-section-title">{category.name}</h2>
              <div className="menu-grid">
                {getProductsByCategory(category.category_id).map(product => {
                  const productVariants = getVariantsByProduct(product.id);
                  const productIngredients = getIngredientsByProduct(product.id);
                  
                  // Calculate minimum price for products with variants
                  const getMinPrice = () => {
                    if (!product.has_varity) {
                      return product.price || 0;
                    }
                    if (productVariants.length > 0) {
                      return Math.min(...productVariants.map(v => v.price || 0));
                    }
                    return 0;
                  };
                  
                  const minPrice = getMinPrice();
                  
                  return (
                    <div key={product.id} className="menu-item">
                      <div className="item-content">
                        <h3 className="item-name">{product.name}</h3>
                        {product.description && <p className="item-description">{product.description}</p>}
                        
                        {/* Show price for all products */}
                        <div className="product-direct">
                          <span>${minPrice.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* For single products - add button on the right */}
                      {!product.has_varity && (
                        <div className="item-right">
                          {cart[product.id] ? (
                            <div className="quantity-controls">
                              <button
                                onClick={() => removeFromCart(product.id)}
                                className="qty-btn remove"
                              >
                                -
                              </button>
                              <span className="qty-display">{cart[product.id]}</span>
                              <button
                                onClick={() => addToCart(product)}
                                className="qty-btn add"
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <button 
                              className="add-button"
                              onClick={() => addToCart(product)}
                            >
                              +
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* For products with variants - show single add button */}
                      {product.has_varity && productVariants.length > 0 && (
                        <div className="item-right">
                          <button 
                            className="add-button"
                            onClick={() => {
                              setSelectedProduct(product);
                              setSelectedVariant(null);
                              setModalQuantity(1);
                              setShowVariantModal(true);
                            }}
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Sidebar */}
      <div className={`order-sidebar ${orderPanelCollapsed ? 'collapsed' : ''}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
      >
        {!orderPanelCollapsed ? (
          <>
            {/* Order Header */}
            <div className="order-header">
              <div className="order-title-section">
                <h2 className="order-title">Current Order</h2>
                <div className="item-count">
                  {getTotalItems()} items
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="order-items">
              {getCartItems().length === 0 ? (
                <div className="empty-order">
                  <div className="empty-icon">🍽️</div>
                  <p className="empty-subtitle">How can we serve you today?</p>
                </div>
              ) : (
                <div className="cart-items">
                  {getCartItems().map(item => (
                    <div key={item.cartKey} className="cart-item">
                      <div className="cart-item-header">
                        <div className="cart-item-info">
                          <h4 className="cart-item-name">{item.name}</h4>
                          {/* Display notes if available */}
                          {item.notes && (
                              <p className="cart-item-notes">
                                  <em>
                                      {Array.isArray(item.notes) 
                                          ? item.notes.join(', ') 
                                          : item.notes
                                      }
                                  </em>
                              </p>
                          )}
                        </div>
                        <div className="cart-item-price">
                          ${item.price.toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="cart-item-controls">
                        <div className="cart-qty-controls">
                          <button
                            onClick={() => removeFromCart(item.cartKey)}
                            className="cart-qty-btn remove"
                          >
                            -
                          </button>
                          <span className="cart-qty">{item.count}</span>
                          <button
                            onClick={() => addToCart(item.product, item.variant, item.notes)}
                            className="cart-qty-btn add"
                          >
                            +
                          </button>
                        </div>
                        
                        <div className="cart-subtotal">
                          ${item.subtotal.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Order Summary & Actions */}
            {getTotalItems() > 0 && (
              <div className="order-summary">
                <div className="summary-content">
                  {/* Totals */}
                  <div className="totals-section">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>${getTotalPrice().toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>Tax (8.0%):</span>
                      <span>${(getTotalPrice() * 0.08).toFixed(2)}</span>
                    </div>
                    <div className="total-final">
                      <div className="final-total">
                        <span>Total:</span>
                        <span className="total-amount">${(getTotalPrice() * 1.085).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button 
                      className="btn-primary"
                      onClick={navigateToSummary}
                    >
                      Review Order
                    </button>
                    <div className="btn-row">
                      <button 
                        onClick={clearCart}
                        className="btn-danger"
                      >
                        Clear Order
                      </button>
                      <button className="btn-secondary save-draft-btn">
                        Save Draft
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : null}
        
        {/* Control Buttons - Always visible */}
        <div className="bottom-controls-bar">
          <div className="bottom-right-buttons">
            {/* Voice Order Button */}
            <button 
            className={`voice-order-btn-bottom ${isListening ? 'listening' : ''}`}
            onClick={isListening ? stopVoiceOrder : startVoiceOrder}
            disabled={!isMenuLoaded.current}
            title={
              !isMenuLoaded.current 
                ? "Loading menu..." 
                : isListening 
                  ? "Stop listening" 
                  : "Start voice order"
            }
          >
            <svg 
              className={isListening ? 'pulse-icon' : ''} 
              width="30" 
              height="30" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          </button>
          
            {/* Toggle Order Panel Button */}
            <button 
              className="order-toggle-btn"
              onClick={() => setOrderPanelCollapsed(!orderPanelCollapsed)}
              title={orderPanelCollapsed ? "Expand order panel" : "Collapse order panel"}
            />
          </div>
          
          {/* Item count display when collapsed */}
          {orderPanelCollapsed && getTotalItems() > 0 && (
            <div className="collapsed-item-count">
              <span className={`count-number ${countAnimating ? 'animating' : ''}`}>
                {getTotalItems()}
              </span>
              <span className="count-text">item{getTotalItems() !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Variant Selection Modal */}
      {showVariantModal && selectedProduct && (
        <div className="modal-overlay" onClick={() => setShowVariantModal(false)}>
          <div className="modal-content variant-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowVariantModal(false)}>×</button>
            
            <h2 className="modal-title">{selectedProduct.name}</h2>
            {selectedProduct.description && (
              <p className="modal-description">{selectedProduct.description}</p>
            )}
            
            <div className="variant-selection">
              <h3 className="selection-title">Size</h3>
              <p className="selection-subtitle">Required • Please select 1</p>
              
              <div className="variant-options">
                {getVariantsByProduct(selectedProduct.id).map((variant) => {
                  const isSelected = selectedVariant?.id === variant.id;
                  
                  return (
                    <button
                      key={variant.id}
                      className={`variant-option-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      <span className="variant-option-name">{variant.name}</span>
                      <span className="variant-option-price">${variant.price.toFixed(2)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="modal-footer">
              <div className="quantity-selector">
                <button 
                  className="qty-adjust" 
                  onClick={() => setModalQuantity(Math.max(1, modalQuantity - 1))}
                >
                  -
                </button>
                <input 
                  type="number" 
                  value={modalQuantity} 
                  min="1" 
                  readOnly
                  className="qty-input"
                />
                <button 
                  className="qty-adjust" 
                  onClick={() => setModalQuantity(modalQuantity + 1)}
                >
                  +
                </button>
              </div>
              
              <button 
                className="add-to-cart-btn"
                onClick={() => {
                  if (selectedVariant) {
                    // Add to cart multiple times based on quantity
                    for (let i = 0; i < modalQuantity; i++) {
                      addToCart(selectedProduct, selectedVariant);
                    }
                    
                    setShowVariantModal(false);
                    setSelectedVariant(null);
                    setModalQuantity(1);
                  }
                }}
                disabled={!selectedVariant}
              >
                Add to Cart {selectedVariant && `$${selectedVariant.price.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderPage;